import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/data';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于芒果视频',
  description: '芒果视频成立于2024年，是一个专注提供免费高清视频资源的平台。了解芒果视频的品牌故事、口碑评价和联系方式。',
};

const orgFullSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '芒果视频',
  url: 'https://www.mangoshipin.cn/',
  description: aboutInfo.slogan,
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    email: aboutInfo.contact.email,
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: aboutInfo.contact.address,
    addressCountry: 'CN',
  },
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 芒果视频"
        description="芒果视频成立于2024年，专注提供免费高清视频资源。了解品牌故事、口碑评价和联系方式。"
        keywords="芒果视频,关于芒果视频,芒果视频品牌,芒果视频口碑,芒果视频联系方式"
        canonicalUrl="https://www.mangoshipin.cn/about"
        ogTitle="关于芒果视频 - 免费高清视频平台"
        ogDescription="芒果视频成立于2024年，专注提供免费高清视频资源。了解品牌故事、口碑评价和联系方式。"
        schema={{ ...aboutSchema, ...orgFullSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full bg-background-50 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-14 md:py-20">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1 text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-900 mb-4">
                  关于 <strong className="text-primary-500">芒果视频</strong>
                </h1>
                <p className="text-lg md:text-xl font-medium text-foreground-600 mb-4">
                  {aboutInfo.slogan}
                </p>
                <p className="text-base text-foreground-500 leading-relaxed max-w-xl">
                  {aboutInfo.intro}
                </p>
              </div>
              <div className="w-full lg:w-[420px] h-64 md:h-72 rounded-2xl overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20orange%20and%20amber%20abstract%20brand%20identity%20with%20soft%20flowing%20curves%2C%20modern%20tech%20company%20atmosphere%2C%20gentle%20cinematic%20lighting%20with%20warm%20highlights%2C%20clean%20minimalist%20aesthetic%2C%20creative%20and%20professional%20composition%2C%20no%20text&width=840&height=560&seq=about-hero-mango&orientation=landscape"
                  alt="芒果视频品牌形象"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                我们的使命与愿景
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-focus-2-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">使命</h3>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">愿景</h3>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  {aboutInfo.vision}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                核心价值观
              </h2>
              <p className="text-sm md:text-base text-foreground-500">
                驱动我们不断前行的信念
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutInfo.values.map((value, index) => {
                const icons = ['ri-heart-line', 'ri-star-line', 'ri-lightbulb-line', 'ri-shield-check-line'];
                return (
                  <div key={index} className="bg-background-100 rounded-2xl p-6 md:p-7 border border-background-200/60 text-center group hover:border-primary-200 transition-all duration-300">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${icons[index]} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-bold font-heading text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                平台口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-500">
                来自各界的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutInfo.reputation.map((rep, index) => (
                <div key={index} className="bg-background-50 rounded-2xl p-6 md:p-7 border border-background-200/60 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-2">{rep.rating}</div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">{rep.platform}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{rep.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-500">
                有任何问题或建议，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-background-100 rounded-2xl p-6 md:p-7 border border-background-200/60 text-center group hover:border-primary-200 transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
              </div>

              <div className="bg-background-100 rounded-2xl p-6 md:p-7 border border-background-200/60 text-center group hover:border-primary-200 transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                  <i className="ri-wechat-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">微信公众号</h3>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
              </div>

              <div className="bg-background-100 rounded-2xl p-6 md:p-7 border border-background-200/60 text-center group hover:border-primary-200 transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-50 text-secondary-600 group-hover:bg-secondary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className="ri-weibo-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">微博</h3>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.weibo}</p>
              </div>
            </div>

            <div className="mt-10 bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60 text-center max-w-md mx-auto">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-foreground-50 text-foreground-600">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-800 mb-2">公司地址</h3>
              <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-8 py-10 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
              开始你的观影之旅
            </h2>
            <p className="text-sm md:text-base text-foreground-500 mb-6">
              加入过亿用户的行列，体验芒果视频带来的纯净观影享受
            </p>
            <Link
              to="/category/recommend"
              className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}